import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  TouchSensor,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { X, GripVertical, Star } from 'lucide-react';

const SortablePhoto = ({ id, src, idx, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    opacity: isDragging ? 0.7 : 1,
  };
  const isHero = idx === 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group rounded-lg overflow-hidden border-2 transition-colors ${
        isHero ? 'border-primary col-span-2 row-span-2 aspect-square' : 'border-transparent hover:border-primary/50 aspect-square'
      } ${isDragging ? 'shadow-2xl ring-2 ring-primary/50' : ''}`}
      data-testid={`reorder-photo-${idx}`}
    >
      <img src={src} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover pointer-events-none select-none" draggable={false}
        onError={(e) => { e.target.style.display = 'none'; }} />

      {/* Drag handle (covers most of the tile so easy to grab on mobile) */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        // `touch-action: none` tells the browser to leave the gesture entirely
        // to dnd-kit's TouchSensor (configured with delay+tolerance). Without
        // this, a long-press drag triggers iOS/Android default page-pan and
        // the user sees the whole canvas slide around instead of just the
        // photo tile they intended to grab.
        style={{ touchAction: 'none' }}
        className="absolute inset-0 cursor-grab active:cursor-grabbing focus:outline-none"
        aria-label={`Drag photo ${idx + 1}`}
        data-testid={`reorder-handle-${idx}`}
      />

      {/* Position badge */}
      <div className={`absolute top-2 left-2 ${isHero ? 'bg-primary text-primary-foreground' : 'bg-black/70 text-white'} text-[11px] font-semibold px-2 py-0.5 rounded-full pointer-events-none inline-flex items-center gap-1`}>
        {isHero ? <><Star className="h-3 w-3" /> Hero</> : `#${idx + 1}`}
      </div>

      {/* Drag-hint icon */}
      <div className="absolute bottom-1.5 left-1.5 bg-black/40 rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <GripVertical className="h-3 w-3 text-white" />
      </div>

      {/* Remove button */}
      <button
        type="button"
        className="absolute top-1.5 right-1.5 w-6 h-6 bg-black/60 hover:bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
        onClick={(e) => { e.stopPropagation(); onRemove(idx); }}
        data-testid={`remove-photo-${idx}`}
        aria-label="Remove photo"
      >
        <X className="h-3 w-3 text-white" />
      </button>
    </div>
  );
};

export const SortablePhotoGrid = ({ photos = [], onChange, max = 60 }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = photos.findIndex(p => p === active.id);
    const newIndex = photos.findIndex(p => p === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(photos, oldIndex, newIndex));
  };

  const handleRemove = (idx) => {
    const next = photos.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  const visible = photos.slice(0, max);

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={visible} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 mb-3" data-testid="sortable-photo-grid">
          {visible.map((src, idx) => (
            <SortablePhoto key={src} id={src} src={src} idx={idx} onRemove={handleRemove} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default SortablePhotoGrid;
